import type { Economy, Province } from '../types/province';
import { formatNumber } from './format';

/**
 * SF-122 Component 3.5: the `AlertBanners.tsx`-replacement logic.
 *
 * Ports `AlertBanners.tsx`'s six warning conditions verbatim (thresholds, messages, and
 * severities unchanged) as a pure, independently-testable function, feeding the new Alerts
 * `<GlanceCard>`/`<AlertRow>` primitives instead of rendering directly.
 */
export interface Alert {
  severity: 'critical' | 'warning';
  message: string;
  /** Optional deep link to the page where the player can act on this alert. */
  actionRoute?: string;
  actionLabel?: string;
}

/**
 * SF-168: days of food remaining at the current drain rate, derived from already-fetched
 * DTO aggregates. Prefers the current month's average daily consumption (more stable than a
 * single tick); falls back to the last tick's consumption when no current-month aggregate is
 * available yet. Returns `null` when there's no meaningful drain rate to divide by.
 */
export function computeDaysOfFood(economy: Economy, currentFood: number): number | null {
  const monthly = economy.currentMonth;
  const avgDailyConsumption =
    monthly && monthly.tickCount > 0
      ? monthly.totalFoodConsumed / monthly.tickCount
      : economy.lastTickChanges.foodConsumed;
  if (avgDailyConsumption <= 0) return null; // no meaningful drain rate to divide by
  return currentFood / avgDailyConsumption;
}

/**
 * SF-168: whether food is critical. `foodNet < 0` alone reads as exactly `0`, not negative, for a
 * province that's been at or near zero food stock for a while (`LazyTickSystem`'s shortfall-clamp
 * branch) — the naive check would go silent exactly when the province is actually starving. Zero
 * stock is always critical regardless of `foodNet`; otherwise critical when net is negative and
 * runway is under 24 ticks (1 Utopian month, docs/TIME.md).
 */
export function isFoodCritical(province: Province): boolean {
  const { resources, economy } = province;
  if (resources.food === 0) return true; // closes the correctness gap: zero stock is always critical
  const daysOfFood = computeDaysOfFood(economy, resources.food);
  return economy.lastTickChanges.foodNet < 0 && daysOfFood !== null && daysOfFood < 24;
}

export function deriveAlerts(province: Province): Alert[] {
  const { economy, military, territory } = province;
  const alerts: Alert[] = [];
  const totalAcres = territory.totals.acres;
  const barrenThreshold = Math.max(20, totalAcres * 0.05);

  if (isFoodCritical(province)) {
    alerts.push({
      severity: 'critical',
      message: 'Food deficit! Starvation imminent.',
      actionRoute: '/buildings',
      actionLabel: 'Build farms',
    });
  }

  if (economy.lastTickChanges.goldNet < 0) {
    alerts.push({
      severity: 'critical',
      message: `Gold deficit: ${formatNumber(economy.lastTickChanges.goldNet)} gc/day.`,
      actionRoute: '/military',
      actionLabel: 'Adjust draft',
    });
  }

  if (economy.buildingEfficiency < 0.70) {
    alerts.push({
      severity: 'critical',
      message: `BE at ${(economy.buildingEfficiency * 100).toFixed(0)}%. Production severely impaired.`,
      actionRoute: '/buildings',
      actionLabel: 'Build homes',
    });
  } else if (economy.buildingEfficiency >= 0.70 && economy.buildingEfficiency < 0.90) {
    alerts.push({
      severity: 'warning',
      message: `BE at ${(economy.buildingEfficiency * 100).toFixed(0)}%. Reduce draft or build homes.`,
      actionRoute: '/buildings',
      actionLabel: 'Build homes',
    });
  }

  if (military.returningArmies.length > 0) {
    alerts.push({ severity: 'warning', message: 'Army is away. Defense reduced.' });
  }

  if (territory.totals.barren > barrenThreshold) {
    alerts.push({
      severity: 'warning',
      message: `${territory.totals.barren} barren acres producing nothing.`,
      actionRoute: '/buildings',
      actionLabel: 'Build on barren land',
    });
  }

  return alerts;
}
